import styled, { keyframes } from 'styled-components';

interface SelectableCardProps {
  $selected: boolean;
}

interface WrapperProps {
  $scrollLocked: boolean;
}

interface ContentProps {
  $footerFixed: boolean;
}

interface FooterProps {
  $fixed: boolean;
}

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

export const Wrapper = styled.section<WrapperProps>`
  position: fixed;
  inset: 0;
  z-index: 20;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  overflow-y: ${({ $scrollLocked }) => ($scrollLocked ? 'hidden' : 'auto')};
  overscroll-behavior: contain;
  background: #fffbf2;
`;

export const Content = styled.div<ContentProps>`
  position: relative;
  width: 100%;
  max-width: 450px;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  padding: calc(env(safe-area-inset-top) + 20px) 20px
    ${({ $footerFixed }) =>
      $footerFixed ? 'calc(env(safe-area-inset-bottom) + 208px)' : 'calc(env(safe-area-inset-bottom) + 32px)'};
  background: #fffbf2;
`;

export const ClosePageButton = styled.button`
  position: absolute;
  top: calc(env(safe-area-inset-top) + 10px);
  right: 12px;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #beb7ad;
  font-size: 32px;
  line-height: 1;
  background: transparent;
  cursor: pointer;
  z-index: 10;

  svg {
    width: 16px;
    height: 16px;
    min-width: 16px;
    min-height: 16px;
  }
`;

export const TitleRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const Title = styled.h2`
  color: #221E1A;
  font-family: var(--ed-font-rounded, ui-rounded, 'SF Pro Rounded', 'SF Pro Text', -apple-system, BlinkMacSystemFont, 'Roboto', sans-serif);
  font-size: 34px;
  font-style: normal;
  font-weight: 800;
  line-height: 41px;
  letter-spacing: -0.07px;
`;

export const TitleIcon = styled.img`
  width: 70px;
  height: 70px;
  min-width: 70px;
  min-height: 70px;
  position: absolute;
  top: -15px;
  left: 50%;
`;

export const Subtitle = styled.p`
  margin-top: 10px;
  color: #030303;
  font-size: 17px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.051px;

  span {
    font-weight: 600;
  }
`;

export const KitsList = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const KitCard = styled.button<SelectableCardProps>`
  width: 100%;
  border-radius: 32px;
  padding: 20px 20px 0;
  text-align: left;
  background: #F2EDE1;
  border: 2px solid ${({ $selected }) => ($selected ? 'var(--ed-brand, #AE66F6)' : '#F2E8DA')};
  transition: border-color 0.2s ease;
  cursor: pointer;
`;

export const KitHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const KitPrice = styled.p`
  color: #221E1A;
  font-size: 13px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: -0.036px;
  text-transform: uppercase;
`;

export const KitSelectCircle = styled.div<SelectableCardProps>`
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  border-radius: 50%;
  border: 2px solid ${({ $selected }) => ($selected ? 'none' : '#7A7670')};
  background: ${({ $selected }) => ($selected ? 'var(--ed-brand, #AE66F6)' : 'transparent')};
  box-shadow: ${({ $selected }) => ($selected ? '1px -3px 7px 0 rgba(255, 255, 255, 0.60) inset' : 'none')};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const KitTitle = styled.h3`
  margin-top: 2px;
  color: #221E1A;
  font-family: var(--ed-font-rounded, ui-rounded, 'SF Pro Rounded', 'SF Pro Text', -apple-system, BlinkMacSystemFont, 'Roboto', sans-serif);
  font-size: 19px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.076px;
`;

export const ProductsList = styled.ul`
  margin-top: 4px;
  padding-left: 16px;

  li {
    list-style: disc;
    color: #7A7670;
    font-size: 15px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.045px;
  }
`;

export const KitImage = styled.img`
  display: block;
  width: 100%;
  max-height: 144px;
  object-fit: contain;
  object-position: center bottom;
  margin-top: 8px;
`;

export const Footer = styled.div<FooterProps>`
  padding-top: 16px;
  background: #fffbf2;
  width: 100%;
  box-sizing: border-box;

  ${({ $fixed }) =>
    $fixed
      ? `
        position: fixed;
        left: 50%;
        right: 0;
        bottom: 0;
        z-index: 25;
        padding: 16px;
        box-shadow: 0 -8px 24px rgba(34, 30, 26, 0.08);
        max-width: 450px;
        transform: translateX(-50%);
      `
      : ''}
`;

export const SummaryRow = styled.div`
  padding: 0 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #221E1A;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.045px;
`;

export const TotalRow = styled.div`
  margin-top: 8px;
  border-radius: 12px;
  background: rgba(190, 226, 188, 0.40);
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  span {
    color: #221E1A;
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0.045px;
  }

  strong {
    color: #29A073;
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0.045px;
  }
`;

export const PayButton = styled.button`
  margin-top: 20px;
  padding: 16px;
  width: 100%;
  min-height: 60px;
  border-radius: 48px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  font-size: 22px;
  line-height: 28px;
  letter-spacing: 0.088px;
  font-weight: 700;
  color: #FFF;
  background: var(--ed-brand, #AE66F6);
  cursor: pointer;

  &:active {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.7;
    pointer-events: none;
  }
`;

export const PayButtonSpinner = styled.img`
  grid-column: 3;
  justify-self: start;
  width: 14px;
  height: 14px;
  margin-left: 6px;
  margin-top: 3px;
  animation: ${spin} 0.8s linear infinite;
`;

export const PayButtonText = styled.span`
  grid-column: 2;
  justify-self: center;
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  touch-action: none;
  background: rgba(35, 30, 25, 0.54);
`;

export const ModalCard = styled.div`
  max-width: 450px;
  position: relative;
  width: 100%;
  border-radius: 24px;
  background: #FFFBF2;
  padding: 32px 20px 30px 20px;
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

export const ModalTitle = styled.h3`
  color: #221E1A;
  font-family: var(--ed-font-rounded, ui-rounded, 'SF Pro Rounded', 'SF Pro Text', -apple-system, BlinkMacSystemFont, 'Roboto', sans-serif);
  font-size: 28px;
  font-weight: 700;
  line-height: 34px;
  letter-spacing: -0.003px;
`;

export const ModalCloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  border-radius: 12px;
  background: #F2EDE1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const ModalText = styled.p`
  margin-top: 10px;
  color: #221E1A;
  font-size: 17px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.051px;
`;

export const EmailField = styled.input`
  margin-top: 22px;
  width: 100%;
  min-height: 60px;
  border-radius: 40px;
  background: #f0eade;
  padding: 12px 12px 12px 24px;
  color: #221E1A;
  text-align: left;
  font-size: 17px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.068px;
`;

export const PrivacyRow = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: flex-start;
  gap: 6px;

  svg {
    width: 17px;
    min-width: 17px;
    height: 18px;
  }
`;

export const PrivacyText = styled.p`
  color: #7A7670;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.036px;

  a {
    text-decoration: underline;
  }
`;

export const SubscribeButton = styled.button`
  margin-top: 20px;
  padding: 16px;
  width: 100%;
  min-height: 60px;
  border-radius: 48px;
  font-size: 22px;
  line-height: 28px;
  letter-spacing: 0.088px;
  font-weight: 700;
  color: #FFF;
  background: var(--ed-brand, #AE66F6);
  cursor: pointer;

  &:active {
    opacity: 0.7;
  }
`;
